import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Accordion, AccordionDetails, AccordionSummary, Stack, styled } from 'shell/components';
import Card from 'shell/components/Card';
import Text from 'shell/components/Text';
import { FileTypes } from 'utils/constants';
import { indexToLetters } from 'utils/helper';

const getViewerCategory = (fileType) => {
    if (fileType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
        return FileTypes.SHEET;
    }
    if (
        fileType === 'application/vnd.openxmlformats-officedocument.presentationml.presentation' ||
        fileType === 'application/vnd.ms-powerpoint'
    ) {
        return FileTypes.PRESENTATION;
    }
    if (fileType === 'application/pdf') {
        return FileTypes.PDF;
    }
    if (fileType?.startsWith('video')) return FileTypes.VIDEO;
    return null;
};

const SupportingEvidence = ({ locations, files, selectedFileId, setSelectedFileId }) => {
    const { t } = useTranslation('assignment');

    const [expanded, setExpanded] = useState(false);

    const currentFile = files.find(({ id }) => id === selectedFileId);

    const viewerCategory = getViewerCategory(currentFile?.type);
    const isClickable = viewerCategory !== FileTypes.SHEET;
    const hasLocations = locations?.length > 0;

    const handleAccordionChange = (_, isExpanded) => {
        setExpanded(isExpanded);
    };

    const renderText = (location) => {
        if (typeof location === 'string') return location;
        switch (viewerCategory) {
            case FileTypes.SHEET: {
                const { col, row } = location.highlight || {};
                const isInvalid = typeof col !== 'number' || typeof row !== 'number';
                if (isInvalid) return location.highlight;
                return t('grading-section.view-in-cell', 'View in Cell {{cell}}', {
                    cell: `${indexToLetters(col)}${row + 1}`,
                });
            }
            case FileTypes.PRESENTATION:
                return t('grading-section.view-in-slide', 'View in Slide {{slide}}', { slide: location.highlight + 1 });
            case FileTypes.PDF:
                return location.highlight;
            case FileTypes.VIDEO:
                return location.highlight;
            default:
                return '';
        }
    };

    if (!viewerCategory) return null;

    return (
        <Stack sx={{ p: 3 }} spacing={2}>
            <Stack spacing={2}>
                <Stack spacing={2} direction={'row'} alignItems={'center'}>
                    <Text>{t('grading-section.supporting-evidence-label', 'Supporting Evidence')}</Text>
                    {hasLocations && (
                        <ItemTextTag variant={'p3'} weight={'medium'}>
                            {t('item-count', '{{count}} items', { count: locations?.length })}
                        </ItemTextTag>
                    )}
                </Stack>
            </Stack>
            <Stack direction={'row'} alignItems={'center'} spacing={2} justifyContent={'space-between'} width={'100%'}>
                <SupportingEvidenceAccordion
                    disabled={!hasLocations}
                    expanded={hasLocations ? expanded : false}
                    onChange={handleAccordionChange}
                    onClick={(e) => e.stopPropagation()}
                >
                    <SupportingEvidenceAccordionSummary>
                        <Text color={'inherit'}>
                            {expanded
                                ? t('grading-section.hide-locations-label', 'Hide Locations')
                                : t('grading-section.view-locations-label', 'View Locations')}
                        </Text>
                    </SupportingEvidenceAccordionSummary>
                    <SupportingEvidenceAccordionDetails>
                        {hasLocations &&
                            locations.map(({ value: location }, index) => {
                                const file = files.find(({ id }) => id === location.file_id);
                                return (
                                    <CustomCard
                                        key={index}
                                        isClickable={isClickable}
                                        onClick={file ? () => setSelectedFileId(location.file_id) : undefined}
                                    >
                                        <LocationTag variant={'p3'} weight={'medium'}>
                                            {file?.name ?? location.file_id}
                                        </LocationTag>
                                        <Text variant={'p3'}>{renderText(location)}</Text>
                                    </CustomCard>
                                );
                            })}
                    </SupportingEvidenceAccordionDetails>
                </SupportingEvidenceAccordion>
                {!hasLocations && (
                    <StatusText>{t('grading-section.location-unavailable', 'Location unavailable')}</StatusText>
                )}
            </Stack>
        </Stack>
    );
};

const CustomCard = styled(Card, {
    shouldForwardProp: (prop) => prop !== 'isClickable',
})(({ theme, isClickable }) => {
    return {
        alignItems: 'flex-start',
        flexDirection: 'column',
        gap: theme.spacing(2.5),
        boxShadow: 'none',
        cursor: isClickable ? 'pointer' : 'default',
        '&:hover': {
            borderColor: !isClickable ? theme.palette.ensightful.neutral[300] : theme.palette.ensightful.primary[500],
            margin: !isClickable ? 'initial' : '-1px',
            borderWidth: !isClickable ? '1px' : '2px',
        },
    };
});

const ItemTextTag = styled(Text)(({ theme }) => {
    return {
        padding: theme.spacing(1, 2),
        backgroundColor: theme.palette.ensightful.primary[50],
        color: theme.palette.ensightful.primary[700],
        borderRadius: '40px',
    };
});

const LocationTag = styled(Text)(({ theme }) => {
    return {
        borderRadius: '40px',
        padding: theme.spacing(1, 2),
        backgroundColor: theme.palette.ensightful.neutral[100],
    };
});

const StatusText = styled(Text)(({ theme }) => {
    return {
        width: '100%',
        textAlign: 'end',
        color: theme.palette.ensightful.error[600],
    };
});

const SupportingEvidenceAccordion = styled(Accordion)(({ theme }) => {
    return {
        width: '100%',
        background: 'inherit',

        '&.Mui-disabled': {
            backgroundColor: theme.palette.ensightful.shades[0],

            '.MuiAccordionSummary-root div': {
                color: theme.palette.ensightful.neutral[400],
            },
        },
    };
});

const SupportingEvidenceAccordionSummary = styled(AccordionSummary)(({ theme }) => {
    return {
        width: 'fit-content',
        '& > div': {
            color: theme.palette.ensightful.primary[500],
        },
    };
});

const SupportingEvidenceAccordionDetails = styled(AccordionDetails)(({ theme }) => {
    return {
        marginTop: theme.spacing(2),
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
    };
});

export default SupportingEvidence;
